import { SchemaTypes } from '@allesauto/graphql';
import translate from '@allesauto/i18n';
import { checkoutSumPrice, formatPrice } from '@allesauto/utils/funcs';
import clsx from 'clsx';
import React, { FC } from 'react';
import { useIntl } from 'react-intl';

import CheckoutAccessories, { AccessoriesProps } from '../checkoutAccessories';
import CheckoutTradeIn from '../checkoutTradeIn';
import TextLink from '../textLink';

interface VehicleProps {
  modelName: string;
  monthlyPrice?: number;
  downPayment?: number;
  totalPrice: number;
  paymentType: SchemaTypes.PaymentType | undefined | null;
  additionalProducts?: { label: string; value: number }[];
  openFinanceOverlay?: () => void;
}

interface TradeInProps {
  name: string;
  price: number;
}

export interface CheckoutVehiclesProps {
  vehicles: VehicleProps[];
  withTradeIn?: boolean;
  tradeInCars?: TradeInProps[];
  trackingId?: string;
  hasAccessoriesShop?: boolean;
  standAloneAccessories: AccessoriesProps[];
}

const CheckoutVehicles: FC<CheckoutVehiclesProps> = (
  props: CheckoutVehiclesProps
) => {
  const {
    vehicles,
    standAloneAccessories,
    withTradeIn = false,
    tradeInCars,
    trackingId,
    hasAccessoriesShop = false,
  } = props;

  const intl = useIntl();
  const trackingIdentifier = trackingId;
  const trackingIdentifierVehicle = `${trackingIdentifier}--vehicle`;
  const trackingIdentifierFinance = `${trackingIdentifier}--finance`;
  const trackingIdentifierMonthlyPrice = `${trackingIdentifier}--monthly-price`;
  const trackingIdentifierPurchasePrice = `${trackingIdentifier}--purchase-price`;
  const trackingIdentifierAdditionalProducts = `${trackingIdentifier}--additional-product`;
  const trackingIdentifierAdditionalProductHeadline = `${trackingIdentifier}--additional-products-headline`;

  return (
    <div data-tracking-id={trackingIdentifier}>
      {vehicles.map((vehicle: VehicleProps, index: number) => {
        const financeTypeText = translate(
          intl,
          `ui.FINANCE_DETAILS.${vehicle.paymentType}`
        );
        return (
          <ul
            key={index}
            className={clsx(
              'border-b-[1px] border-grey30 columns-1 font-regular leading-5 px-15px py-15px text-p1 lg:pb-35px lg:pt-40px lg:px-40px',
              index === vehicles.length - 1 && 'lg:pb-20px'
            )}
          >
            <li
              className="font-headline font-weight-bold leading-6 mb-20px text-headline4"
              data-tracking-id={`${trackingIdentifierVehicle}${index}`}
            >
              {vehicle.modelName}
            </li>
            {vehicle.monthlyPrice && vehicle.monthlyPrice !== 0 && (
              <li
                className="flex justify-between mb-5px"
                data-tracking-id={`${trackingIdentifierMonthlyPrice}${index}`}
              >
                <p className="table-cell">
                  {translate(intl, 'ui.CHECKOUT_SIDEBAR.MONTHLY_PAYMENT_TITLE')}
                </p>
                <p className="table-cell">
                  {formatPrice(intl, vehicle.monthlyPrice, {
                    withFractionDigits: true,
                  })}
                </p>
              </li>
            )}

            <li
              className={`flex !justify-between ${
                !vehicle.monthlyPrice ? 'mb-30px' : 'mb-5px'
              }`}
              data-tracking-id={`${trackingIdentifierPurchasePrice}${index}`}
            >
              <p className="table-cell">
                {!vehicle.monthlyPrice
                  ? translate(intl, 'ui.CHECKOUT_SIDEBAR.PURCHASE_PRICE_TITLE')
                  : translate(intl, 'ui.CHECKOUT_SIDEBAR.DOWN_PAYMENT_TITLE')}
              </p>
              <p className="table-cell">
                {formatPrice(
                  intl,
                  vehicle.downPayment !== undefined
                    ? vehicle.downPayment
                    : vehicle.totalPrice,
                  {
                    withFractionDigits: true,
                  }
                )}
              </p>
            </li>

            {vehicle.monthlyPrice && vehicle.paymentType && (
              <li
                className="flex justify-end mb-30px"
                data-tracking-id={`${trackingIdentifierFinance}-${financeTypeText}`}
              >
                <TextLink
                  data-
                  text={translate(
                    intl,
                    'ui.FINANCE_DETAILS.FINANCE_DETAILS_TEXT',
                    {
                      financeType: financeTypeText,
                    }
                  )}
                  onClick={vehicle.openFinanceOverlay}
                  icon={null}
                  customClassName="text-p3"
                  textColor="text-lightPurple monochrome:text-grey60"
                  fillColor="fill-lightPurple monochrome:fill-grey60"
                />
              </li>
            )}

            {vehicle.additionalProducts &&
              vehicle.additionalProducts.length > 0 && (
                <>
                  <li
                    className="flex font-weight-bold justify-between mb-5px lg:mb-5px"
                    data-tracking-id={
                      trackingIdentifierAdditionalProductHeadline
                    }
                  >
                    <p className="table-cell">
                      {translate(
                        intl,
                        'ui.CHECKOUT_SIDEBAR.ADDITIONAL_PRODUCTS_TITLE'
                      )}
                    </p>
                    {vehicle.additionalProducts.length > 1 && (
                      <p className="table-cell">
                        {formatPrice(
                          intl,
                          checkoutSumPrice(vehicle.additionalProducts),
                          {
                            withFractionDigits: true,
                          }
                        )}
                      </p>
                    )}
                  </li>
                  {vehicle.additionalProducts.map(
                    (
                      product: {
                        label: string;
                        value: number;
                      },
                      index: number
                    ) => (
                      <li
                        key={index}
                        className="flex justify-between mb-5px lg:mb-5px"
                        data-tracking-id={`${trackingIdentifierAdditionalProducts}${index}`}
                      >
                        <p className="table-cell">{product.label}</p>
                        <p className="ml-10px table-cell">
                          {formatPrice(intl, product.value, {
                            withFractionDigits: true,
                          })}
                        </p>
                      </li>
                    )
                  )}
                </>
              )}

            {index === vehicles.length - 1 && (
              <p className="text-grey60 text-p2 sm:mt-20px md:mt-40px">
                {translate(intl, 'ui.PRICE.ALL_PRICES_INCLUDE_VAT')}
              </p>
            )}
          </ul>
        );
      })}
      {hasAccessoriesShop && (
        <CheckoutAccessories checkoutAccessories={standAloneAccessories} />
      )}
      {withTradeIn &&
        tradeInCars &&
        tradeInCars.map((tradeIn, index) => (
          <CheckoutTradeIn
            key={`trade-in-${index}`}
            modelName={tradeIn.name}
            tradeInPrice={tradeIn.price}
          />
        ))}
    </div>
  );
};

export default CheckoutVehicles;
